import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DeviceFilters} from '../model/filters/device-filters';
import {HttpUtils} from '../shared/http.utils';
import {Observable} from 'rxjs';
import {DevicesResponse} from '../model/responses/devices-response';
import {DevicePersistRequest} from '../model/requests/device-persist-request';
import {DeviceMeasurement} from '../model/device-measurement';
import {DeviceMeasurementFilters} from '../model/filters/device-measurement-filters';
import {DeviceExpressionChecker} from '../model/device-expression-checker';
import {CommandExec, CommandsSchemas} from '@iotsens/commands';
import {Response} from 'src/app/model/responses/response';
import {DeviceRemovedFilters} from '../model/filters/device-removed-filters';
import {DevicesRemovedResponse} from '../model/responses/devices-removed-response';

@Injectable({
  providedIn: 'root'
})
export class DevicesService {

  private readonly devicesURL: string;

  constructor(private http: HttpClient) {
    this.devicesURL = '/catalog/devices';
  }

  public getDevice(deviceUuid: string) {
    let url = `${this.devicesURL}/${deviceUuid}`;
    return this.http.get<any>(url);
  }

  public createDevice(creationData: DevicePersistRequest) {
    let url = `${this.devicesURL}/create`;
    return this.http.post<any>(url, creationData);
  }

  public editDevice(editionData: DevicePersistRequest) {
    let url = `${this.devicesURL}/edit`;
    return this.http.post<any>(url, editionData);
  }

  public deleteDevice(deviceUuid: string) {
    let url = `${this.devicesURL}/${deviceUuid}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public deleteDevices(uuids: string[]) {
    let url = `${this.devicesURL}/delete`;
    return this.http.post<any>(url, uuids, {observe: 'response'});
  }

  public getDevices(offset?: number, limit?: number, filters?: DeviceFilters): Observable<DevicesResponse> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    let body = (filters != null) ? filters : new DeviceFilters();
    return this.http.post<DevicesResponse>(this.devicesURL, body, options);
  }

  public getDevicesFromUuid(devicesUuid: string[], offset?: number, limit?: number): Observable<DevicesResponse> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    let url = `${this.devicesURL}/getFromUuid`;
    return this.http.post<DevicesResponse>(url, devicesUuid, options);
  }

  public getDeviceAssets(deviceUuid: string) {
    let url = `${this.devicesURL}/${deviceUuid}/assets`;
    return this.http.get<any>(url);
  }

  public getDeviceProperties(deviceUuid: string) {
    let url = `${this.devicesURL}/${deviceUuid}/properties`;
    return this.http.get<any>(url);
  }

  public getDeviceMeasurement(deviceMeasurementUuid: string) {
    let url = `${this.devicesURL}/measurements/${deviceMeasurementUuid}`;
    return this.http.get<any>(url);
  }

  public getDeviceMeasurements(deviceMeasurementUuids: string[]) {
    let url = `${this.devicesURL}/measurements`;
    return this.http.post<any>(url, deviceMeasurementUuids);
  }

  public getDeviceMeasurementsOfDevice(deviceUuid: string, measurementTypeSensor?: string){
    let url: string = `${this.devicesURL}/${deviceUuid}/measurements`;
    const options: { headers?: any, params?: any } = measurementTypeSensor ?
      HttpUtils.buildOptions({measurementTypeSensor: measurementTypeSensor}) : {};

    return this.http.get<any>(url, {...options, observe: 'body'});
  }

  public async getDeviceMeasurementsOfDeviceFilteredByMeasurementTypeSensor(deviceUuid: string, measurementTypeSensor: string) {
    const response: {
      deviceMeasurements: DeviceMeasurement[]
    } = await this.getDeviceMeasurementsOfDevice(deviceUuid, measurementTypeSensor).toPromise();

    response.deviceMeasurements.forEach((measurement: DeviceMeasurement): void => {
      if (measurement.alias !== undefined) {
        measurement.name = measurement.alias;
      }
    });
    return response.deviceMeasurements.sort((a, b) => a.name > b.name ? 1 : -1);
  }

  public getDeviceMeasurementsOfDevices(deviceUuids: string[]) {
    let url = `${this.devicesURL}/measurements-of-devices`;
    return this.http.post<any>(url, deviceUuids);
  }

  public getCommonDeviceMeasurements(filters: DeviceMeasurementFilters, offset?: number, limit?: number) {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    let url = `${this.devicesURL}/commonMeasurements`;
    return this.http.post<any>(url, filters, options);
  }

  public getDeviceIntegrationModules(deviceUuid: string) {
    let url = `${this.devicesURL}/${deviceUuid}/integration_modules`;
    return this.http.get<any>(url);
  }

  public getDeviceTriggers(deviceUuid: string, offset?: number, limit?: number) {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    let url = `${this.devicesURL}/${deviceUuid}/triggers`;
    return this.http.get<any>(url, options);
  }

  public getDeviceSummaries() {
    let url = `${this.devicesURL}/summary`;
    return this.http.get<any>(url);
  }

  public updateDisplayInAsset(measurements: DeviceMeasurement[], newState: boolean) {
    let options = HttpUtils.buildOptions({newState: newState});
    let url = `${this.devicesURL}/update_display_in_asset`;
    return this.http.post<any>(url, measurements, options);
  }

  public checkExpression(deviceExpressionChecker: DeviceExpressionChecker) {
    const url = `${this.devicesURL}/checkSensorExpression`;
    return this.http.post<any>(url, deviceExpressionChecker);
  }

  public importDevicesFromCsv(files: File[], tenant: string, organization: string) {
    const url = `${this.devicesURL}/import-csv`;
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('tenant', tenant);
    formData.append('organization', organization);

    return this.http.post<void>(url, formData);
  }

  public getDeviceCommandSchemasByUuid(deviceUuid: string): Observable<CommandsSchemas> {
    const url = `${this.devicesURL}/${deviceUuid}/schemas`;
    return this.http.get<CommandsSchemas>(url);
  }

  public executeDeviceCommand(deviceUuid: string, schemaName: string, commandName: string, commandExec: CommandExec): Observable<Response> {
    const url = `${this.devicesURL}/${deviceUuid}/schemas/${schemaName}/commands/${commandName}`;
    return this.http.post<Response>(url, commandExec.parameters);
  }

  public getDevicesRemoved(offset?: number, limit?: number, filters?: DeviceRemovedFilters): Observable<DevicesRemovedResponse> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    let body = (filters != null) ? filters : new DeviceRemovedFilters();
    return this.http.post<DevicesRemovedResponse>(`${this.devicesURL}/devicesRemoved`, body, options);
  }

}
